import React, { useState } from 'react';
import Axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPAInput from '../../shared/SPAInput';
import { useSPAToast } from '../../shared/SPAToast';
import { useCopySection, useLessonSectionRows } from './useSections';

// The legacy Make a Copy modal (common/CopyButton): the copy goes to the lesson whose id is typed,
// this lesson by default, so a section can be copied into any program.
const SectionCopyModal = () => {
  const navigate = useNavigate();
  const { lessonId, programId, sectionId } = useParams<{ lessonId: string; programId: string; sectionId: string }>();
  const pid = Number(programId);
  const lid = Number(lessonId);
  const id = Number(sectionId);
  const [targetLessonId, setTargetLessonId] = useState(lessonId ?? '');
  const spaToast = useSPAToast();
  const { data: rows = [] } = useLessonSectionRows(lid);
  const copy = useCopySection(pid, lid);
  const title = rows.find(row => row.id === id)?.full_title ?? 'this section';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    copy.mutate({ id, targetLessonId: Number(targetLessonId) }, {
      onSuccess: () => {
        spaToast.success(`Copied “${title}” to lesson ${targetLessonId}.`);
        navigate('..');
      },
    });
  };

  let error: string | null = null;
  if (copy.isError) {
    const data = Axios.isAxiosError(copy.error) ? copy.error.response?.data : undefined;
    error = data?.code === 'RESOURCE_NOT_FOUND' ? 'Lesson ID does not exist.' : 'There was a problem copying.';
  }

  return (
    <SPADialog open onOpenChange={(open) => { if (!open) navigate('..'); }}>
      <SPADialog.Content size="sm">
        <form onSubmit={submit}>
          <SPADialog.Header>
            <SPADialog.Title>Make a Section Copy</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              Copies “{title}” into the lesson with this ID.
            </SPADialog.Description>
            <div>
              <label className="tw-mb-1 tw-block tw-text-xs tw-text-spa-secondary" htmlFor="section-copy-lesson-id">
                Lesson ID
              </label>
              <SPAInput
                required
                id="section-copy-lesson-id"
                inputMode="numeric"
                min={1}
                type="number"
                value={targetLessonId}
                onChange={event => setTargetLessonId(event.target.value)}
              />
            </div>
            {error && <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">{error}</p>}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={copy.isLoading || !targetLessonId} type="submit" variant="primary">
              {copy.isLoading ? 'Copying…' : 'Make a Copy'}
            </SPAButton>
          </SPADialog.Footer>
        </form>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default SectionCopyModal;
